import lodash from 'lodash';
import React from 'react';
import { MandatoryTopicSessionStep, SessionFieldsFragment, useGetProjectComplianceQuery } from '../../../../../../generated/core-api/graphql-react-query';
import { AudioWebhookResponse } from '../../../SessionConnectionContextProvider/useSessionConnectionContext';
import useComplianceNotifications from '../useComplianceNotifications';
import { SessionComplianceChecklistItem } from './type';

export default function useSessionCompliance(lastMessage?: AudioWebhookResponse | null, session?: SessionFieldsFragment, projectId?: string | null): SessionComplianceChecklistItem[] {
  // TODO: this could potentially be in a provider
  const topics = useGetProjectComplianceQuery({ input: { id: projectId ?? '' } }, { enabled: !!projectId });

  const [mentionedTopics, setMentionedTopics] = React.useState<string[]>([]);
  const [conversationEngaged, setConversationEngaged] = React.useState<boolean>(false);
  const [sessionDuration, setSessionDuration] = React.useState<number>(0);

  React.useEffect(() => {
    if (session?.id) {
      setSessionDuration(0);
      const interval = setInterval(() => {
        setSessionDuration((prev) => prev + 1000);
      }, 1000);
      return () => clearInterval(interval);
    }
    setSessionDuration(0);
    return () => { };
  }, [session?.id]);

  React.useEffect(() => {
    setMentionedTopics([]);
    setConversationEngaged(false);
  }, [session?.id]);

  const complianceChecklist = React.useMemo(
    () => {
      if (!topics.data) return [];
      return topics.data.getProject.mandatoryTopics
        .filter((topic) => {
          if (mentionedTopics.includes(topic.textLower)) return true;
          if (topic.minimumDuration && sessionDuration > (topic.minimumDuration * 60000)) return true;
          if (topic.sessionStepCondition === MandatoryTopicSessionStep.ConversationEngaged && conversationEngaged) return true;
          if (topic.conditionTopics && topic.conditionTopics.every((conditionTopic) => mentionedTopics.includes(conditionTopic.textLower))) return true;
          return false;
        })
        .map((topic) => ({
          ...topic,
          isCompleted: mentionedTopics.includes(topic.textLower),
          requiredAt: Date.now(),
        }));
    },
    [topics.data, sessionDuration, conversationEngaged, mentionedTopics],
  );
  const sessionTopics = React.useRef(complianceChecklist);
  React.useEffect(() => {
    if (!lodash.isEqual(sessionTopics.current, complianceChecklist)) {
      sessionTopics.current = complianceChecklist;
    }
  }, [complianceChecklist]);

  useComplianceNotifications(!!session, sessionTopics.current);

  React.useEffect(() => {
    if (lastMessage) {
      switch (lastMessage.type) {
        case 'detection':
          if (lastMessage.message.detection.topicTextLower) {
            setMentionedTopics((prev) => prev.concat(lastMessage.message.detection.topicTextLower));
          }
          break;
        case 'callStart':
          setConversationEngaged(true);
          break;
        default:
          break;
      }
    }
  }, [lastMessage]);

  return complianceChecklist;
}
